/* eslint-disable unicorn/no-null */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { IActor } from '@newo/api/gateway';
import { isPlainObject } from 'es-toolkit';
import type { IChatPersona } from '@/modules/Chat/Chat.types.ts';

export type TSandboxPoint = { x: number; y: number };
export type TSandboxSize = { width: number; height: number };

export const SANDBOX_DEFAULT_POSITION: TSandboxPoint | null = null;
export const SANDBOX_DEFAULT_SIZE: TSandboxSize = {
    width: 380,
    height: 600
};

// Persist schema version. It is independent from the application release version.
const SANDBOX_STORE_VERSION = 1;

type IChatState = {
    userPersona?: IChatPersona;
    setUserPersona: (userPerson?: IChatPersona) => void;
    userActor?: IActor;
    setUserActor: (userActor?: IActor) => void;
    agentPersona?: IChatPersona;
    setAgentPersona: (agentPerson?: IChatPersona) => void;
    agentActor?: IActor;
    setAgentActor: (agentActor?: IActor) => void;
    toggleChat: () => void;
    isChatOpened: boolean;

    position: TSandboxPoint | null;
    setPosition: (pos: TSandboxPoint) => void;
    size: TSandboxSize;
    setSize: (size: TSandboxSize) => void;

    flush: () => void;
};

const initialState = {
    isChatOpened: false,
    userPersona: undefined,
    userActor: undefined,
    agentPersona: undefined,
    agentActor: undefined,
    position: SANDBOX_DEFAULT_POSITION,
    size: SANDBOX_DEFAULT_SIZE
};

const useSandboxStore = create<IChatState>()(
    persist(
        (set) => ({
            ...initialState,
            setUserPersona: (userPersona) => {
                set({ userPersona });
            },
            setUserActor: (userActor) => {
                set({ userActor });
            },
            setAgentPersona: (agentPersona) => {
                set({ agentPersona });
            },
            setAgentActor: (agentActor) => {
                set({ agentActor });
            },
            toggleChat: () => {
                set((state) => ({ isChatOpened: !state.isChatOpened }));
            },
            setPosition: (position) => {
                set({ position });
            },
            setSize: (size) => {
                set({ size });
            },
            flush: () => {
                set(initialState);
            }
        }),
        {
            name: 'sandbox-store',
            storage: createJSONStorage(() => globalThis.localStorage),
            partialize: (state) => state,

            version: SANDBOX_STORE_VERSION,
            migrate: (persisted) => {
                const state = isPlainObject(persisted) ? persisted : {};
                return { ...state, position: null };
            }
        }
    )
);

export default useSandboxStore;
